import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ResponsiveDialog from "../../components/ResponsiveDialog";
import { PricingState } from "./PricingState";
import { Selection } from "./types";

type Props = {
  /** The self row, or null when this property is not tracking itself yet. */
  selection: Selection | null;
  onAdd: () => void;
  /** Stop tracking the property itself. Confirmed here, not by the caller. */
  onDelete: (item: Selection) => void;
  /**
   * A write is in flight somewhere on the page, so every control here disables —
   * not only the one that started it. Same reason as `SelectedList.busyId`: the
   * deletes cascade over the shared price and mapping tables and two at once
   * deadlocked on the server.
   */
  busy: boolean;
  /** This row specifically is the one being deleted, for the dialog's spinner. */
  deleting: boolean;
};

/**
 * The customer's own property, tracked as a competitor so it can be compared.
 *
 * ⚠️ **Above the compset, and separated from it.** It is not one of the competitors —
 * it does not count against the allowance and it is not something the client chose
 * from a map — so listing it among them would make the count read wrong.
 *
 * ⚠️ Two states and no third. Either the property tracks itself, in which case this
 * says how its pricing is doing and offers to stop, or it does not and there is one
 * thing to do about that. A contract Revbell has not matched yet fails on the way in
 * with a sentence saying so, which is better than a disabled button with no
 * explanation.
 */
const SelfProperty = ({
  selection,
  onAdd,
  onDelete,
  busy,
  deleting,
}: Props) => {
  const intl = useIntl();
  /** Whether the confirmation is open. One row, so a boolean says all of it. */
  const [confirming, setConfirming] = useState(false);

  // Named once and used twice: MUI applies a tooltip's title as the accessible name
  // of the element it clones — here the `<span>`, which only exists because a
  // disabled button cannot receive the hover events the tooltip needs. `aria-label`
  // on a bare span is ignored, leaving the button with no accessible name.
  // ⚠️ The shared `DELETE`, not the compset list's "Remove from compset": this row
  // is deliberately not part of the compset, so that label would describe the wrong
  // thing. The confirmation says what actually goes.
  const deleteLabel = intl.formatMessage({ id: "DELETE" });
  const name = selection?.shortName || selection?.name || "";

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="subtitle2">
            <FormattedMessage id="COMPETITOR_SELF_TITLE" />
          </Typography>
          {selection && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Chip
                size="small"
                variant="outlined"
                sx={{ borderRadius: "5px" }}
                label={<FormattedMessage id="COMPETITOR_SELF_TRACKED" />}
              />
              <Tooltip title={deleteLabel}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={deleteLabel}
                    disabled={busy}
                    onClick={() => setConfirming(true)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        {selection ? (
          <Box>
            <Typography variant="body2">{name}</Typography>
            {/* ⚠️ Two different questions, and only one of them is worth asking at a
                time. While onboarding is unfinished, `PricingState` reports on a connector
                that may not exist yet — "no price" is true and misleading, because the
                reason is that nothing has run, not that the property is unpriceable.
                Once it reaches `READY` the pricing badge is the honest answer and the
                onboarding step has nothing left to say. */}
            {selection.onboardingStatus &&
            selection.onboardingStatus !== "READY" ? (
              <Chip
                size="small"
                variant="outlined"
                sx={{ borderRadius: "5px" }}
                label={
                  <FormattedMessage
                    id={`COMPETITOR_ONBOARDING_${selection.onboardingStatus}`}
                  />
                }
              />
            ) : (
              <PricingState item={selection} />
            )}
          </Box>
        ) : (
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <FormattedMessage id="COMPETITOR_SELF_EMPTY" />
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={busy}
              onClick={onAdd}
            >
              <FormattedMessage id="COMPETITOR_SELF_ADD" />
            </Button>
          </Stack>
        )}
      </Stack>

      <ResponsiveDialog
        fullWidth
        open={confirming}
        title={deleteLabel}
        // Shared with the compset list rather than a self-specific copy: what is
        // removed, and what survives centrally, is the same either way.
        contentText={intl.formatMessage(
          { id: "COMPETITOR_SELECTION_REMOVE_CONFIRM" },
          { name }
        )}
        onClose={() => setConfirming(false)}
        onSubmit={() => {
          if (selection) onDelete(selection);
          setConfirming(false);
        }}
        submitLabel={intl.formatMessage({ id: "COMPETITOR_SELECTION_REMOVE" })}
        loading={deleting}
      />
    </Paper>
  );
};

export default SelfProperty;
